var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AppConfig } from '../../config/config';
/**
 * Get country code from Shopify, default to "au" if not available
 */
function getCountryCode() {
    var _a;
    return (typeof window !== 'undefined' && ((_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.country))
        ? window.Shopify.country.toLowerCase()
        : 'au';
}
/**
 * Encode a string for use in Fredhopper location query
 * Special characters must be encoded as \uXXXX
 */
function encodeCriterion(str) {
    // Delimiters that need encoding: @ !"#$%&'()*+,-./:;<=>[]^{}|`~
    const delimiterMap = {
        ' ': '\\u0020',
        '!': '\\u0021',
        '"': '\\u0022',
        '#': '\\u0023',
        '/': '\\u002f',
        '@': '\\u0040',
        ':': '\\u003A',
    };
    return str.replace(/[ !"#/@:]/g, (char) => delimiterMap[char] || char);
}
/**
 * Convert UI sort values to Fredhopper sort values
 * UI values: "relevance", "price-asc", "price-desc", "title-asc", "title-desc"
 * Fredhopper values: "price_XX" (ascending), "-price_XX" (descending),
 * "custom_style_name" (name ascending), etc.
 */
function convertSortValue(sortValue) {
    if (!sortValue || sortValue === 'relevance') {
        return undefined; // No sort param means relevance
    }
    // Get country code for price sorting
    const countryCode = getCountryCode();
    // Handle price sorting
    if (sortValue === 'price-asc' || sortValue === 'price_asc') {
        return `price_${countryCode}`;
    }
    if (sortValue === 'price-desc' || sortValue === 'price_desc') {
        return `-price_${countryCode}`;
    }
    // Handle title sorting
    if (sortValue === 'title-asc' || sortValue === 'title_asc') {
        return 'custom_style_name';
    }
    if (sortValue === 'title-desc' || sortValue === 'title_desc') {
        return '-custom_style_name';
    }
    // If it's already a valid Fredhopper sort value (starts with @ or -, or is a known attribute), pass it through
    // Otherwise, return undefined to use default relevance
    if (sortValue.startsWith('@') || sortValue.startsWith('-') || /^[a-z_]+$/i.test(sortValue)) {
        return sortValue;
    }
    return undefined;
}
/**
 * Build Fredhopper location query string
 * Format: //universe/locale/attributes
 */
function buildLocationQuery(request, defaultUniverse, defaultLocale) {
    const universe = request.universe || defaultUniverse;
    const locale = request.locale || defaultLocale;
    // Get country code for price filters
    const countryCode = getCountryCode();
    const parts = [];
    // Build search query if provided
    // Note: special characters like "//" are rejected from AWS API Gateway.
    // We must URI encode the entire query string after all parameters are added to ensure proper transmission through API Gateway.
    if (request.q && request.q !== '*') {
        const encodedQuery = encodeCriterion(request.q);
        let searchPart = `$s=${encodedQuery}`;
        if (request.searchPass) {
            searchPart += `;p=${request.searchPass}`;
        }
        if (request.searchProfile) {
            searchPart += `;t=${request.searchProfile}`;
        }
        parts.push(searchPart);
    }
    // Build attribute filters
    // Callers may suffix the filterId with `::<basetype>` (e.g. `custom_birdsnest_tv::text`)
    // so we know which Fredhopper criterion syntax to emit. Without it we'd default to
    // `>{...}` which Fredhopper silently ignores for single-select facets, returning the
    // unfiltered set. See ./README.md for the convention.
    if (request.filters) {
        Object.entries(request.filters).forEach(([rawFilterId, values]) => {
            if (values.length) {
                const [filterId, basetype = 'set'] = rawFilterId.split('::');
                const isPriceRange = filterId === 'price' || filterId === `price_${countryCode}`;
                const hasRangeSeparator = /[,-]/.test(values[0]);
                // Float-range facets (e.g. Rating) whose options come from Fredhopper as
                // pre-built criteria like "3.0<reviews_group_rating<3.99" — sent as-is,
                // with no `attr>{...}` wrapper. Wrapping this string in braces (the
                // multi-select set syntax below) produces a criterion Fredhopper doesn't
                // recognise and silently returns zero results for.
                const rangeCriterionPattern = new RegExp(`^-?[\\d.]+<${filterId}<-?[\\d.]+$`);
                const isRangeCriterion = values.every((v) => rangeCriterionPattern.test(v));
                if (isPriceRange && hasRangeSeparator) {
                    // Price range: min,max or min-max; use country-specific price field
                    const [min, max] = values[0].split(/[,-]/);
                    parts.push(`price_${countryCode}>${min}`);
                    parts.push(`price_${countryCode}<${max}`);
                }
                else if (isRangeCriterion) {
                    // Pre-built range criterion(s), OR'd together: {crit1;crit2}
                    parts.push(values.length > 1 ? `{${values.join(';')}}` : values[0]);
                }
                else if (basetype === 'text') {
                    // text basetype = single-select: attr=value (no braces)
                    parts.push(`${filterId}=${encodeCriterion(values[0])}`);
                }
                else {
                    // Multiple values: attribute>{value1,value2} (OR logic)
                    const valueList = values.map(encodeCriterion).join(';');
                    parts.push(`${filterId}>{${valueList}}`);
                }
            }
        });
    }
    // Build category/collection filter. Skip for `style-matches` — that's a
    // virtual collection handled by the Peacock middleware (which injects a
    // `secondid<{...}` filter server-side), not a real Fredhopper category.
    if (request.collection && request.collection !== 'style-matches') {
        parts.push(`categories<{${request.collection}}`);
    }
    // Build location string
    const attributes = parts.length > 0 ? `/${parts.join('/')}` : '';
    return `//${universe}/${locale}${attributes}`;
}
export function fetchFredhopper(r) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const { apiUrl, siteId, locale, displayFieldsMode } = AppConfig.searchSettings.fredhopper;
        // Get universe from config (siteId) or use default
        const universe = siteId || 'catalog01';
        const defaultLocale = locale || 'en_GB';
        // Get country code for market parameter
        const countryCode = getCountryCode();
        // Build location query
        const locationQuery = buildLocationQuery(r, universe, defaultLocale);
        // Build URL parameters
        const params = new URLSearchParams();
        // Core parameters
        params.append('fh_location', locationQuery);
        // Market parameter (uppercase country code)
        params.append('market', countryCode.toUpperCase());
        // View template
        if (r.view) {
            params.append('fh_view', r.view);
        }
        // Pagination (convert page/pageSize to fh_start_index/fh_view_size)
        if (r.page !== undefined) {
            const pageSize = r.pageSize || 24;
            const startIndex = (r.page - 1) * pageSize;
            params.append('fh_start_index', String(startIndex));
            params.append('fh_view_size', String(pageSize));
        }
        else if (r.pageSize) {
            params.append('fh_view_size', String(r.pageSize));
        }
        // Sorting - convert UI sort values to Fredhopper sort values
        const fredhopperSort = convertSortValue(r.sort);
        if (fredhopperSort) {
            // fh_sort_by accepts attribute_type or $function_attribute_type
            // Support descending with - prefix
            params.append('fh_sort_by', fredhopperSort);
        }
        // Response suppression
        if (r.suppress && r.suppress.length > 0) {
            params.append('fh_suppress', r.suppress.join(','));
        }
        // Reporting parameters
        if (r.session) {
            params.append('fh_session', r.session);
        }
        if (r.refView) {
            params.append('fh_refview', r.refView);
        }
        if (r.refTheme) {
            params.append('fh_reftheme', r.refTheme);
        }
        if (r.refFacet) {
            params.append('fh_reffacet', r.refFacet);
        }
        if (r.refSearch) {
            params.append('fh_refsearch', r.refSearch);
        }
        // Detail/Compare page parameters
        if (r.secondid) {
            const secondids = Array.isArray(r.secondid) ? r.secondid : [r.secondid];
            secondids.forEach(id => {
                params.append('fh_secondid', id);
            });
        }
        if (r.secondid2) {
            params.append('fh_secondid2', r.secondid2);
        }
        // Facet control
        if (r.facets && r.facets.length > 0) {
            params.append('fh_facets', r.facets.join(','));
        }
        if (r.maxDisplayNrValues) {
            Object.entries(r.maxDisplayNrValues).forEach(([facetName, maxValues]) => {
                params.append(`fh_maxdisplaynrvalues_${facetName}`, String(maxValues));
            });
        }
        // Display fields mode
        if (displayFieldsMode) {
            params.append('fh_displayfields_mode', displayFieldsMode);
        }
        // Logging
        if (r.log === false) {
            params.append('fh_log', '0');
        }
        // Themes/Campaigns
        if (r.noThemes) {
            params.append('fh_nothemes', '1');
        }
        // Modifications
        if (r.allowedModification && r.allowedModification.length > 0) {
            params.append('fh_allowed_modification', r.allowedModification.join(','));
        }
        if (r.disableRedirect) {
            params.append('fh_disable_redirect', 'true');
        }
        // A/B Testing (Since FAS 19.2)
        if (r.anid) {
            params.append('fh_anid', r.anid);
        }
        if (r.appId) {
            params.append('fh_app_id', r.appId);
        }
        if (r.userSeed !== undefined) {
            params.append('fh_user_seed', String(r.userSeed));
        }
        if (r.sessionId) {
            params.append('fh_session_id', r.sessionId);
        }
        if (r.abTests) {
            params.append('fh_abtests', r.abTests);
        }
        // `debug=yes` asks Fredhopper to return debug-mode data (extra diagnostics
        // on the response). Gated on `window.mrPLPConfig.debug` so the flag is only
        // ever sent from environments that opt in.
        if (typeof window !== 'undefined' && ((_a = window.mrPLPConfig) === null || _a === void 0 ? void 0 : _a.debug)) {
            params.append('debug', 'yes');
        }
        const queryString = params.toString();
        let url = '';
        // Style Matches PLP is a special collection that relies on Peacock API from Birdsnest.
        // Peacock API returns a list of product IDs with the heart_score values.
        // Our middleware API will fetch the products from Fredhopper API and add the heart_score values to the products.
        if (r.collection === 'style-matches') {
            const styleMatchesApiUrl = process.env.STYLE_MATCHES_API_URL || '';
            // Forward all the same Fredhopper params as /fredhopper/query so the
            // middleware can apply filter/sort/pagination server-side, plus optional
            // `matches-type`. Customer identity is resolved by the middleware from the
            // Shopify app proxy's `logged_in_customer_id` — no `email` is sent.
            const styleMatchesParams = new URLSearchParams(queryString);
            if (r.matchesType) {
                styleMatchesParams.append('matches-type', r.matchesType);
            }
            // Resolve against the page origin so STYLE_MATCHES_API_URL can be a
            // path-only value (e.g. `/apps/api-proxy/public/v1/customers/style-matches`)
            // and the request hits the same host as the storefront. Fully-qualified
            // URLs (`https://…`) pass through unchanged.
            const styleMatchesUrl = new URL(styleMatchesApiUrl, window.location.origin);
            styleMatchesUrl.search = styleMatchesParams.toString();
            url = styleMatchesUrl.toString();
        }
        else {
            url = `${apiUrl}/query?${queryString}`;
        }
        const response = yield fetch(url, {
            headers: {
                'Accept': 'application/json',
                'Accept-Encoding': 'gzip',
            },
        });
        if (!response.ok) {
            throw new Error(`Fredhopper API error: ${response.statusText}`);
        }
        // Expose what the theme's staff "Clear Cache" buttons need (BDN-946). Only the
        // lister query represents the PLP grid — detail/recs calls also go through this
        // function, so guard on view. Published on the existing window.mrPLPConfig:
        //   - cacheKey:      the query currently on screen ("Clear page cache"); the
        //                    X-FH-Cache-Key the middleware returned (CORS-exposed).
        //   - cacheCollection: the collection id this query used, i.e. the value the
        //                    middleware prefixes the cache key with. "Clear all queries
        //                    in page" sends this so the server clears every cached
        //                    permutation of the collection. Undefined for the
        //                    style-matches PLP (a virtual collection, no category id).
        if (r.view === 'lister' && typeof window !== 'undefined') {
            const plpConfig = (window.mrPLPConfig = window.mrPLPConfig || {});
            plpConfig.cacheKey = response.headers.get('X-FH-Cache-Key') || undefined;
            plpConfig.cacheCollection =
                r.collection && r.collection !== 'style-matches' ? r.collection : undefined;
        }
        return response.json();
    });
}
export function fetchFredhopperSuggestions(query) {
    return __awaiter(this, void 0, void 0, function* () {
        const { apiUrl, siteId, locale, displayFieldsMode } = AppConfig.searchSettings.fredhopper;
        // Get universe from config (siteId) or use default
        const universe = siteId || 'catalog01';
        const defaultLocale = locale || 'en_GB';
        const countryCode = getCountryCode();
        const params = new URLSearchParams({
            search: query,
            scope: `//${universe}/${defaultLocale}`
        });
        // Market parameter (uppercase country code)
        params.append('market', countryCode.toUpperCase());
        if (displayFieldsMode) {
            params.append('fh_displayfields_mode', displayFieldsMode);
        }
        const url = `${apiUrl}/suggest?${params.toString()}`;
        const response = yield fetch(url, {
            headers: {
                'Accept': 'application/json',
                'Accept-Encoding': 'gzip',
            },
        });
        if (!response.ok) {
            throw new Error(`Fredhopper Suggest API error: ${response.statusText}`);
        }
        return response.json();
    });
}
